import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Crown, Inbox, ListTodo, FileText, Users, AlertTriangle, Clock,
  Shield, Loader2, Calendar, Target, Trophy, Activity, Building2, BookOpen,
} from 'lucide-react';
import DailyBriefing from './DailyBriefing';
import TodosPanel from './TodosPanel';
import NotesPanel from './NotesPanel';
import ContactsPanel from './ContactsPanel';
import MeetingsPanel from './MeetingsPanel';
import ProjectsPanel from './ProjectsPanel';
import GoalsPanel from './GoalsPanel';
import ActivityFeed from './ActivityFeed';
import CalendarConnect from './CalendarConnect';
import PropertiesPanel from './PropertiesPanel';
import HandbookPanel from './HandbookPanel';
import './ceo-life.css';

const TABS = [
  { id: 'briefing', label: 'Daily Briefing', Icon: Inbox },
  { id: 'todos', label: 'To-Dos', Icon: ListTodo },
  { id: 'meetings', label: 'Meetings', Icon: Calendar },
  { id: 'projects', label: 'Projects', Icon: Target },
  { id: 'properties', label: "Adam's Way", Icon: Building2 },
  { id: 'goals', label: 'Goals', Icon: Trophy },
  { id: 'notes', label: 'Notes', Icon: FileText },
  { id: 'handbook', label: 'Handbook', Icon: BookOpen },
  { id: 'contacts', label: 'Contacts', Icon: Users },
  { id: 'activity', label: 'Activity', Icon: Activity },
];

export default function CeoLifePage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('briefing');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await api.get('/ceo-life/access');
      setMembers(data || []);
    } catch (err) {
      if (err?.response?.status === 403) setDenied(true);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    if (!user) return;
    if (!user.ceo_life_access) {
      setDenied(true);
      setLoading(false);
      return;
    }
    fetchMembers();
  }, [user, fetchMembers]);

  if (loading) {
    return (
      <div className="ceo-shell flex items-center justify-center min-h-screen">
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: 'var(--accent)' }} />
      </div>
    );
  }

  if (denied) {
    return (
      <div className="ceo-shell flex items-center justify-center px-6" data-testid="ceo-life-denied">
        <div className="ceo-card-elevated max-w-md w-full p-8 text-center" style={{ borderRadius: 14 }}>
          <Shield className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--gold)' }} />
          <h2 className="ceo-headline text-2xl mb-2">Access restricted</h2>
          <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
            CEO Life is a private workspace. Only authorized members can enter.
            If you should have access, contact the CEO directly.
          </p>
          <button onClick={() => navigate('/dashboard')} className="ceo-btn ceo-btn-ghost mt-5">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ceo-shell ceo-grid-bg relative" data-testid="ceo-life-page">
      <div className="ceo-noise"/>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">

        {/* Header */}
        <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="h-4 w-4" style={{ color: 'var(--gold)' }} />
              <span className="ceo-eyebrow">Command center</span>
            </div>
            <h1 className="ceo-headline text-4xl sm:text-5xl">
              CEO Life
            </h1>
            <p className="text-sm mt-2" style={{ color: 'var(--text-dim)' }}>
              {greeting()}, {user?.name?.split(' ')[0] || 'CEO'}.
              <span style={{ color: 'var(--text-low)' }}> {todayLong()}</span>
            </p>
          </div>

          {/* Workspace + Calendar */}
          <div className="flex flex-col items-end gap-3">
            <CalendarConnect api={api} />
            {members.length > 0 && (
              <div className="flex flex-col items-end gap-2" data-testid="ceo-members-chip">
                <span className="ceo-eyebrow">Workspace</span>
                <div className="flex items-center gap-2">
                  {members.slice(0, 3).map((m, i) => (
                    <div
                      key={m.id}
                      title={`${m.name} (${m.email})`}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2"
                      style={{
                        background: `linear-gradient(135deg, var(--accent-2), var(--accent))`,
                        borderColor: 'var(--ink)',
                        color: '#0F0F18',
                        marginLeft: i > 0 ? -8 : 0,
                        zIndex: 10 - i,
                      }}
                    >
                      {(m.name || m.email).charAt(0).toUpperCase()}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-7 overflow-x-auto pb-1" data-testid="ceo-tabs">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`ceo-tab-btn ${tab === id ? 'active' : ''}`}
              data-testid={`ceo-tab-${id}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <main className="space-y-6">
          {tab === 'briefing' && <DailyBriefing api={api} onJump={setTab} />}
          {tab === 'todos' && <TodosPanel api={api} />}
          {tab === 'meetings' && <MeetingsPanel api={api} />}
          {tab === 'projects' && <ProjectsPanel api={api} />}
          {tab === 'properties' && <PropertiesPanel api={api} />}
          {tab === 'goals' && <GoalsPanel api={api} />}
          {tab === 'notes' && <NotesPanel api={api} />}
          {tab === 'handbook' && <HandbookPanel api={api} />}
          {tab === 'contacts' && <ContactsPanel api={api} />}
          {tab === 'activity' && (
            <div className="ceo-card-elevated p-5" data-testid="ceo-activity-tab">
              <div className="flex items-center gap-2 mb-3">
                <Activity className="h-4 w-4" style={{ color: 'var(--accent)' }}/>
                <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>Workspace activity</h3>
                <span className="ceo-pill" style={{ padding:'2px 8px', fontSize: 10 }}>Auto-refreshes every 30s</span>
              </div>
              <ActivityFeed api={api} limit={100} />
            </div>
          )}
        </main>

        <footer className="mt-12 pt-6 text-center" style={{ borderTop: '1px solid var(--line)' }}>
          <p className="ceo-eyebrow flex items-center justify-center gap-1.5">
            <Shield className="h-3 w-3" /> Private workspace · {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </footer>
      </div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function todayLong() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
